import { IPoint2d } from '@/editor/math/IPoint2d';
import { booleanPolygon, BooleanPolygonType } from '@/editor/model/polygon';
import { BackSide, FrontSide, Mesh, Path, Shape, ShapeGeometry } from 'three';
import { IBaseDataContourFace, IFaceData } from '@/editor/data/face/face';
import { pixelRatio3d } from '@/editor/math/const';
import BaseModel3d from '../BaseModel3d';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { createMaterial } from '@/editor/data/material/utils';
import { globalStores } from '@/editor/stores';
import { EFaceSide } from '@/editor/types';
import { dispersePath } from '@/editor/utils/shape';

type TContourFaceData = Omit<IFaceData, 'holes'>;

export class ContourFaceModel extends BaseModel3d {
  constructor(options: { id: string }) {
    super({ id: options.id });

    this.model.userData.type = EEntityType.Face;
    this.model.userData.containerType = EContainerType.entity;
    this.model.userData.entityId = this._id;

    globalStores.editorStateStore.sceneInfo.scene3d.intersectableGroup.add(this.model);
  }

  /** 洞由调用方传入。faceData.holes 是墙面门窗洞的比例坐标，与这里的绝对坐标不是一回事 */
  createModel(faceData: TContourFaceData, holes: IPoint2d[][] = []) {
    if (!this.canUpdate) return;

    this.clearModel();

    const planeMesh = this.createPlane(faceData, holes);

    planeMesh.position.set(0, 0, (faceData.baseData as IBaseDataContourFace).offGround / pixelRatio3d);

    // 添加到容器
    this.model.add(planeMesh);
  }

  private calPlaneHoleOutlines(holes: IPoint2d[][], outline: IPoint2d[]) {
    const holePointsList: IPoint2d[][] = [];
    for (const points of holes) {
      const holePoints: IPoint2d[] = [];
      points.forEach((point) =>
        holePoints.push({
          x: Number((point.x / pixelRatio3d).toFixed(6)),
          y: Number((point.y / pixelRatio3d).toFixed(6))
        })
      );
      holePointsList.push(holePoints);
    }

    const resultHoles = booleanPolygon({
      main: [outline],
      assistant: holePointsList,
      type: BooleanPolygonType.Intersect
    });

    // 定义孔洞多边形
    const holeOutlines: IPoint2d[][] = [];
    for (const resultHole of resultHoles) {
      const holeoutline: IPoint2d[] = [];
      for (const point of resultHole) {
        holeoutline.push({ x: Number(point.x.toFixed(6)), y: Number(point.y.toFixed(6)) });
      }

      holeOutlines.push(holeoutline);
    }

    return holeOutlines;
  }

  private createPlane(faceData: TContourFaceData, holes: IPoint2d[][]) {
    const baseData = faceData.baseData as IBaseDataContourFace;
    const side = faceData.relativeInfo.side;
    const contour = baseData.contour;

    const impreciseOutlone = dispersePath(contour).map((pt) => ({
      x: Number((pt.x / pixelRatio3d).toFixed(6)),
      y: Number((pt.y / pixelRatio3d).toFixed(6))
    }));

    const shape = new Shape();
    impreciseOutlone.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point.x, point.y);
      } else {
        shape.lineTo(point.x, point.y);
      }
    });

    // 添加孔洞（后续环）
    const holeOutlines = this.calPlaneHoleOutlines(holes, impreciseOutlone);
    for (const holeOutline of holeOutlines) {
      const holePath = new Path();
      holeOutline.forEach((point, index) => {
        if (index === 0) {
          holePath.moveTo(point.x, point.y);
        } else {
          holePath.lineTo(point.x, point.y);
        }
      });
      shape.holes.push(holePath);
    }

    const material = createMaterial(
      faceData.materialInfo.id,
      false,
      side === EFaceSide.BottomSide ? BackSide : FrontSide
    );

    // 测试颜色
    // material.color = new Color(0xff0000);

    const geometry = new ShapeGeometry(shape);
    const planeMesh = new Mesh(geometry, material);

    return planeMesh;
  }

  updateWithoutDestroy(faceData: TContourFaceData, holes: IPoint2d[][] = []) {
    if (!this.canUpdate) return;

    this.createModel(faceData, holes);
  }
}
